<template>
	<b-card footer-bg-variant="dark" footer-border-variant="dark">
		<h4 class="card-title">Preview</h4>
		<b-aspect :aspect="aspectRatio" class="preview mb-2" ref="previewAspect">
			<div class="preview-inner" :style="previewInnerStyle">
				<slot :previewAudio="previewAudio">
					<p class="text-muted mb-0 text-center coming-soon">Coming soon</p>
				</slot>
			</div>
			<div class="preview-bottom-bar">
				<div class="preview-controls">
					<b-button-group size="sm">
						<b-button :variant="aspectRatio === '16:9' ? 'primary' : 'dark'" @click.stop="aspectRatio = '16:9'">16:9</b-button>
						<b-button :variant="aspectRatio === '1:1'  ? 'primary' : 'dark'" @click.stop="aspectRatio = '1:1'">1:1</b-button>
					</b-button-group>
					<b-button-group size="sm" class="ml-2">
						<b-button v-for="res in resolutions" :key="res.label" :variant="selectedWidth === res.width ? 'primary' : 'dark'" @click.stop="selectedWidth = res.width">{{ res.label }}</b-button>
					</b-button-group>
				</div>
				<button v-if="showAudio" class="preview-audio-btn" @click.stop="previewAudio = !previewAudio" :title="previewAudio ? 'Disable audio preview' : 'Enable audio preview'">
					<b-icon :icon="previewAudio ? 'volume-up-fill' : 'volume-mute-fill'" font-scale="1.4" />
				</button>
			</div>
		</b-aspect>
		<slot name="options" />
		<template #footer>
			<widget-url :url="widgetUrl" />
		</template>
	</b-card>
</template>

<style lang="scss">
@import '@/plugins/variable.scss';

.preview {
	background: repeating-conic-gradient(#666 0% 25%, #999 0% 50%) 0 / 32px 32px;
}

.preview .b-aspect-content {
	position: relative;
	overflow: hidden;
}

.preview .preview-inner {
	position: absolute;
	inset: 0;

	> .coming-soon {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		filter: drop-shadow(0 1px 1px $gray-200) drop-shadow(0 -1px 1px $gray-200) drop-shadow(1px 0 1px $gray-200) drop-shadow(-1px 0 1px $gray-200);
		color: $body-color;
		white-space: nowrap;
	}
}

.preview-bottom-bar {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 0.4rem 0.5rem;
	display: flex;
	align-items: center;
	background: transparent;
	transition: background 0.15s;
}

.preview:hover .preview-bottom-bar {
	background: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, transparent 100%);
}

.preview-controls {
	opacity: 0;
	transition: opacity 0.15s;
	pointer-events: none;
	display: flex;
	align-items: center;
}

.preview:hover .preview-controls {
	opacity: 1;
	pointer-events: auto;
}

.preview-audio-btn {
	margin-left: auto;
	background: rgba(0, 0, 0, 0.45);
	border: none;
	border-radius: 4px;
	color: #fff;
	padding: 3px 7px;
	line-height: 1;
	cursor: pointer;
	opacity: 0.4;
	transition: opacity 0.15s;
	flex-shrink: 0;
}

.preview:hover .preview-audio-btn {
	opacity: 1;
}

.preview-audio-btn:hover {
	background: rgba(0, 0, 0, 0.65);
}

.preview-slider-label { min-width: 3.5rem; }
.preview-slider-value { min-width: 5.5rem; text-align: right; }
</style>

<script>
import WidgetUrl from '@/components/Widget/WidgetUrl.vue';

export default {
	components: { WidgetUrl },
	props: {
		slug: String,
		name: String,
		defaultWidth: { type: Number, default: 0 },
		showAudio: { type: Boolean, default: false },
	},
	data() {
		return {
			aspectRatio: '16:9',
			previewActualWidth: 400,
			selectedWidth: this.defaultWidth,
			previewAudio: false,
			resolutions: [
				{ label: 'Fit',  width: 0    },
				{ label: '480p', width: 854  },
				{ label: 'HD',   width: 1280 },
				{ label: 'FHD',  width: 1920 },
			],
		};
	},
	computed: {
		widgetUrl() {
			let vm = this.$parent;
			while(vm) {
				if(vm.user?.widget_hash !== undefined) break;
				vm = vm.$parent;
			}
			const hash = vm?.user?.widget_hash || '';
			return `${this.$baseUrl}/widget/${this.slug}.html?hash=${encodeURIComponent(hash)}`;
		},
		virtualWidth() {
			return this.selectedWidth || this.previewActualWidth;
		},
		virtualHeight() {
			const [w, h] = this.aspectRatio.split(':').map(Number);
			return Math.round(this.virtualWidth * h / w);
		},
		previewActualHeight() {
			const [w, h] = this.aspectRatio.split(':').map(Number);
			return Math.round(this.previewActualWidth * h / w);
		},
		scaleFactor() {
			return Math.min(
				this.previewActualWidth  / this.virtualWidth,
				this.previewActualHeight / this.virtualHeight,
			);
		},
		previewInnerStyle() {
			if(!this.selectedWidth) return {};
			return {
				width:           `${this.virtualWidth}px`,
				height:          `${this.virtualHeight}px`,
				transform:       `scale(${this.scaleFactor})`,
				transformOrigin: 'top left',
			};
		},
	},
	mounted() {
		this.previewActualWidth = this.$refs.previewAspect.$el.clientWidth;
		this.resizeObserver = new ResizeObserver(entries => {
			this.previewActualWidth = entries[0].contentRect.width;
		});
		this.resizeObserver.observe(this.$refs.previewAspect.$el);
	},
	beforeDestroy() {
		this.resizeObserver?.disconnect();
	},
};
</script>
